import { ReactNode, useEffect, useRef, useState } from "react"
import { Text, Button, Input } from "../../../components"
import { extractErrorDetail } from "../../../helpers"
import { API_BASE_URL, SHOW_MAPS, SHOW_POINT } from '../../../config'
import starIcon from '../../../assets/icons/star-icon.svg'
import { LocationPicker, LatLng, parseLocation, toYandexMapsLink } from './location-picker'

const PROMO_SLIDES = SHOW_POINT ? [
    {
        title: 'Размещение на карте',
        text: 'Партнёры отображаются на карте сервиса. Конкурентов там меньше, ваши социальные сети доступны прямо с карты, а новые клиенты находят вас поблизости.',
    },
    {
        title: 'Рейтинг от посетителей',
        text: 'На вашей странице появится рейтинг. Посетители ставят оценку без перехода по дополнительным ссылкам. Рейтинг отображается на карте и привлекает внимание всех её пользователей.',
    },
    {
        title: 'Скидочные баллы',
        text: 'За подписку на ваши социальные сети пользователи получают баллы и могут потратить их на скидку у вас (1 балл = 1 рубль). Клиент не может накопить и потратить больше баллов, чем установлено вами. Так вы привлекаете новых клиентов и подписчиков.',
    },
    {
        title: 'Гибкое управление',
        text: 'Количество баллов можно изменить, а продвижение - отключить в любой момент.',
    },
] :
[
    {
        title: 'Размещение на карте',
        text: 'Партнёры отображаются на карте сервиса. Конкурентов там меньше, ваши социальные сети доступны прямо с карты, а новые клиенты находят вас поблизости.',
    },
    {
        title: 'Рейтинг от посетителей',
        text: 'На вашей странице появится рейтинг. Посетители ставят оценку без перехода по дополнительным ссылкам. Рейтинг отображается на карте и привлекает внимание всех её пользователей.',
    },
    {
        title: 'Гибкое управление',
        text: 'Точку на карте можно изменить, а продвижение - отключить в любой момент.',
    },
    {
        title: 'Перспектива',
        text: 'Подключайте и другие функции для продвижения, которые мы будем добавлять в будущем.',
    },
] 

interface PartnerStore {
    id: number;
    isPartner?: boolean;
    spending_points?: number;
    // Это location магазина ("lat,lon"), бэк отдаёт его под таким именем
    raiting?: string | null;
}

interface ShellProps {
    title: string;
    titleAction?: ReactNode;
    subtitle?: string;
    active?: boolean;
    onClose: () => void;
    children: ReactNode;
}

// Шапка с «Назад» и hero-блок
const Shell = ({title, titleAction, subtitle, active, onClose, children}: ShellProps) => {
    return (
        <div className="admin-partner-page">
            <div className="admin-partner-page-head">
                <button type="button" className="admin-edit-btn" onClick={onClose}>
                    <Text size="xs" color="lightGray">← Назад</Text>
                </button>
                {titleAction}
            </div>

            <div className="admin-partner-hero">
                <div className="admin-partner-hero-icon">
                    <img src={starIcon} alt="" />
                </div>
                <span className={`admin-partner-status${active ? ' admin-partner-status-active' : ''}`}>
                    {active ? 'Продвижение активно' : 'Продвижение не подключено'}
                </span>
                <Text size="xl" color="white">{title}</Text>
                {subtitle && <Text size="s" color="lightGray">{subtitle}</Text>}
            </div>

            {children}
        </div>
    )
}

interface PartnerPromoProps {
    store: PartnerStore;
    token: string;
    onClose: () => void;
    onChanged: () => Promise<{raiting?: string | null} | undefined | void> | void;
    onUnauthorized?: () => void;
}

export const PartnerPromo = ({store, token, onClose, onChanged, onUnauthorized}: PartnerPromoProps) => {
    const [points, setPoints] = useState(String(store.spending_points ?? 0))
    const [busy, setBusy] = useState(false)
    const [confirmDisable, setConfirmDisable] = useState(false)
    const [showInfo, setShowInfo] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [saved, setSaved] = useState(false)
    // Пустая ссылка — локацию не трогаем; locationRemoved — удалить текущую
    const [locationLink, setLocationLink] = useState('')
    const [locationRemoved, setLocationRemoved] = useState(false)
    const [showPicker, setShowPicker] = useState(false)
    const [pickedPoint, setPickedPoint] = useState<LatLng | null>(null)
    const locationRef = useRef<HTMLDivElement>(null)

    // Пришли по ссылке «Укажите адрес» — сразу открываем выбор точки
    useEffect(() => {
        if (window.location.hash !== '#location' || !store.isPartner) return
        setShowPicker(true)
        locationRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const authHeaders = {Authorization: `Bearer ${token}`}

    // false — сессия истекла, дальше не продолжаем
    const send = async (url: string, method: 'GET' | 'POST') => {
        const res = await fetch(url, {method, headers: authHeaders})
        if (res.status === 401) {
            onUnauthorized?.()
            return false
        }
        if (!res.ok) throw new Error((await extractErrorDetail(res)) ?? '')
        return true
    }

    const setPartner = async (value: boolean) => {
        setConfirmDisable(false)
        setBusy(true)
        setError(null)
        try {
            if (await send(`${API_BASE_URL}/partner-store?store_id=${store.id}&value=${value}`, 'GET')) await onChanged()
        } catch (err) {
            setError(err instanceof Error && err.message ? err.message : 'Не удалось сохранить')
        } finally {
            setBusy(false)
        }
    }

    const pointsValue = Math.max(0, Math.floor(Number(points) || 0))
    const pointsChanged = pointsValue !== (store.spending_points ?? 0)

    const changePoints = (delta: number) => {
        setSaved(false)
        setPoints(String(Math.max(0, pointsValue + delta)))
    }

    const link = locationLink.trim()
    const removeLocation = !link && locationRemoved && !!store.raiting
    const locationChanged = SHOW_MAPS && (!!link || removeLocation)

    const save = async () => {
        setBusy(true)
        setError(null)
        try {
            if (pointsChanged && !await send(`${API_BASE_URL}/update-store-point?store_id=${store.id}&value=${pointsValue}`, 'POST')) return

            if (locationChanged) {
                const params = new URLSearchParams({store_id: String(store.id)})
                if (link) params.set('link', link)
                if (!await send(`${API_BASE_URL}/update-store-location?${params}`, 'POST')) return
            }

            const updated = await onChanged()
            // Бэк молча сбрасывает локацию, если не смог достать координаты из ссылки
            if (link && updated && !updated.raiting) {
                setError('Не удалось определить координаты по ссылке — откройте место в Яндекс Картах или 2ГИС и скопируйте ссылку с меткой')
                return
            }
            setLocationLink('')
            setLocationRemoved(false)
            setPickedPoint(null)
            setShowPicker(false)
            setSaved(true)
        } catch (err) {
            setError(err instanceof Error && err.message ? err.message : 'Не удалось сохранить')
        } finally {
            setBusy(false)
        }
    }

    if (!store.isPartner || showInfo) {
        return (
            // «Назад» из описания возвращает к настройкам, а не уводит со страницы
            <Shell
                title="Продвижение"
                subtitle="Привлекайте больше новых клиентов и создавайте повод вернуться к вам снова"
                active={store.isPartner}
                onClose={store.isPartner ? () => setShowInfo(false) : onClose}
            >
                <div className="admin-profile-edit">
                    <div className="admin-partner-benefits">
                        {PROMO_SLIDES.map((s, i) => (
                            <div className="admin-partner-benefit" key={s.title}>
                                <span className="admin-partner-benefit-num">{i + 1}</span>
                                <Text size="m" color="white">{s.title}</Text>
                                <Text size="s" color="lightGray">{s.text}</Text>
                            </div>
                        ))}
                    </div>

                    {error && <Text size="xs" color="accent">{error}</Text>}

                    {/* ст. 10.1 152-ФЗ: точка на карте — распространяемые ПД, согласие даётся при подключении */}
                    {!store.isPartner && SHOW_MAPS && (
                        <span className="admin-login-consent jost">
                            Подключая продвижение, я даю <a href="/consent-distribution" target="_blank" rel="noopener noreferrer">согласие на распространение</a> сведений моей страницы и указанной мной точки на карте сервиса. Убрать точку или отключить продвижение можно в любой момент
                        </span>
                    )}

                    {store.isPartner ? (
                        <Button type="button" variant="solid" fullWidth textSize="s" onClick={() => setShowInfo(false)}>
                            Понятно
                        </Button>
                    ) : (
                        <Button type="button" variant="solid" fullWidth textSize="s" onClick={() => setPartner(true)} disabled={busy}>
                            {busy ? 'Подключение…' : 'Подключить продвижение'}
                        </Button>
                    )}
                </div>
            </Shell>
        )
    }

    const hasLocation = !!store.raiting && !locationRemoved
    const locationStatus =
        link ? 'Новая точка — сохраните, чтобы применить' :
        removeLocation ? 'Будет убрано с карты' :
        hasLocation ? 'Вы отмечены на карте' :
        'Вас пока нет на карте'

    const removeFromMap = () => {
        setSaved(false)
        setLocationLink('')
        setPickedPoint(null)
        setLocationRemoved(true)
        setShowPicker(false)
    }

    return (
        <Shell
            title="Настройки продвижения"
            subtitle={SHOW_POINT ? "Управляйте баллами и местом на карте — изменения применятся после сохранения" : "Управляйте местом на карте — изменения применятся после сохранения"}
            active
            titleAction={
                <button type="button" className="admin-modal-close admin-modal-info" aria-label="Что такое продвижение" onClick={() => setShowInfo(true)}>i</button>
            }
            onClose={onClose}
        >
            <div className="admin-profile-edit">
                {SHOW_POINT && <div className="admin-partner-section">
                    <Text size="m" color="white">Скидочные баллы для клиентов</Text>
                    <Text size="xs" color="lightGray">{'Сколько баллов хотите отдать клиентам за подписки. \n (1 балл - 1 рубль, сумма скидки на всех клиентов не превышает указанного вами числа)'}</Text>
                    <div className="admin-partner-stepper">
                        <button type="button" className="admin-partner-step" aria-label="Уменьшить" onClick={() => changePoints(-10)} disabled={busy || pointsValue <= 0}>−</button>
                        <input
                            className="admin-partner-points-input"
                            type="number"
                            inputMode="numeric"
                            min={0}
                            aria-label="Скидочные баллы"
                            value={points}
                            onChange={(e) => {setSaved(false); setPoints(e.target.value)}}
                        />
                        <button type="button" className="admin-partner-step" aria-label="Увеличить" onClick={() => changePoints(10)} disabled={busy}>+</button>
                    </div>
                </div>}

                {SHOW_MAPS && (
                    <div className="admin-partner-section" id="location" ref={locationRef}>
                        <Text size="m" color="white">Место на карте</Text>
                        <Text size="xs" color="lightGray">По точке на карте новые клиенты найдут вас рядом с собой</Text>
                        <div className="admin-partner-row">
                            {hasLocation && !link ? (
                                <a href={`/map/${store.id}`} target="_blank" rel="noreferrer" className="admin-partner-row-text">{locationStatus}</a>
                            ) : (
                                <span className="admin-partner-row-text">{locationStatus}</span>
                            )}
                            {removeLocation ? (
                                <button type="button" className="admin-location-remove" onClick={() => setLocationRemoved(false)}>Вернуть</button>
                            ) : (
                                <button type="button" className="admin-location-remove" onClick={() => setShowPicker((v) => !v)}>
                                    {showPicker ? 'Свернуть' : hasLocation ? 'Изменить' : 'Указать'}
                                </button>
                            )}
                        </div>

                        {showPicker && (
                            <>
                                <LocationPicker
                                    initial={locationRemoved ? null : parseLocation(store.raiting)}
                                    point={pickedPoint}
                                    onPick={(point) => {
                                        setSaved(false)
                                        setLocationRemoved(false)
                                        setPickedPoint(point)
                                        setLocationLink(toYandexMapsLink(point))
                                    }}
                                />
                                <Input
                                    placeholder="или вставьте ссылку из Яндекс Карт / 2ГИС"
                                    value={pickedPoint ? '' : locationLink}
                                    onChange={(e) => {setSaved(false); setPickedPoint(null); setLocationLink(e.target.value)}}
                                />
                                {hasLocation && (
                                    <button type="button" className="admin-partner-link" onClick={removeFromMap}>Убрать с карты</button>
                                )}
                            </>
                        )}
                    </div>
                )}

                {error && <Text size="xs" color="accent">{error}</Text>}

                <Button type="button" variant="solid" fullWidth textSize="s" onClick={save} disabled={busy || !(pointsChanged || locationChanged)}>
                    {busy ? 'Сохранение…' : saved && !pointsChanged && !locationChanged ? 'Сохранено' : 'Сохранить'}
                </Button>

                {confirmDisable ? (
                    <div className="admin-partner-confirm">
                        <Text size="xs" color="lightGray">Отключить продвижение?</Text>
                        <button type="button" className="admin-location-remove" onClick={() => setPartner(false)} disabled={busy}>Да</button>
                        <button type="button" className="admin-partner-link" onClick={() => setConfirmDisable(false)} disabled={busy}>Нет</button>
                    </div>
                ) : (
                    <button type="button" className="admin-partner-link admin-partner-disable" onClick={() => setConfirmDisable(true)} disabled={busy}>
                        Отключить продвижение
                    </button>
                )}
            </div>
        </Shell>
    )
}
